import type { VoiceMessage } from './types.js';

type Msgs = ReadonlyArray<VoiceMessage>;

export interface DisplayOrder {
  /** Índices de `messages` en el orden REAL de la charla. */
  readonly order: ReadonlyArray<number>;
  /**
   * Posición DENTRO de `order` donde va la burbuja viva del coach: justo
   * delante de los mensajes que el user despachó mientras el coach sonaba y
   * que todavía esperan el commit de esa burbuja. Igual a `order.length`
   * cuando nadie le ganó de mano — o sea, el comportamiento de siempre: la
   * burbuja viva va al final del hilo.
   */
  readonly liveCoachAt: number;
}

/**
 * Orden REAL de la charla a partir del array (append-only) de `messages`.
 *
 * El array se llena por orden de COMMIT, no por orden de habla: la burbuja
 * del coach recién se commitea en el `turn-end` acústico (para no cortar el
 * karaoke), así que un mensaje que el user manda antes de que el coach termine
 * de hablar aterriza ARRIBA del coach aunque el coach hablara primero — y
 * encima con el texto del coach ya completo en pantalla.
 *
 * Acá se deshace esa inversión SIN tocar el array: los mensajes marcados con
 * {@link VoiceMessage.sentWhileCoachSpeaking} se bajan por debajo de la
 * primera burbuja del coach que aparece después de ellos (su commit). Que el
 * array siga siendo append-only no es un detalle: los índices son estables,
 * así que las keys de React reordenan nodos en vez de remontarlos, y todo el
 * estado posicional que las apps mantienen por índice sigue alineado.
 */
export function displayOrder(messages: Msgs): DisplayOrder {
  const order: number[] = [];
  // Mensajes que le ganaron de mano al coach: esperan su commit para bajar.
  let pending: number[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    if (m === undefined) continue;
    if (m.sentWhileCoachSpeaking === true) {
      pending.push(i);
      continue;
    }
    order.push(i);
    if (m.who === 'bot' && pending.length > 0) {
      order.push(...pending);
      pending = [];
    }
  }
  // Lo que quedó pendiente es la carrera EN CURSO: la burbuja viva del coach
  // va justo antes (es donde va a caer su commit).
  const liveCoachAt = order.length;
  order.push(...pending);
  return { order, liveCoachAt };
}
